import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Quote, DEFAULT_QUOTES } from '../../utils/welcomePopupDefaults';
import './WelcomePopup.css';

const QUOTES_STORAGE_KEY = 'welcome_popup_quotes';
const ENABLED_STORAGE_KEY = 'welcome_popup_enabled';
const SEEN_KEY = 'welcome_popup_seen';
const MARK_SRC = `${process.env.PUBLIC_URL || ''}/brand/carmen-lorraine-mark.svg`;

const hasSeenWelcome = (): boolean => {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const rememberWelcome = () => {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Private browsing can block storage. The message still shows for this visit.
  }
};

const WelcomePopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [greeting, setGreeting] = useState<string>('Welcome');

  useEffect(() => {
    checkAndShowPopup();

    const handleQuotesUpdate = () => checkAndShowPopup();
    const handleEnabledUpdate = () => checkAndShowPopup();
    const handleStorage = (event: StorageEvent) => {
      if (event.key === QUOTES_STORAGE_KEY || event.key === ENABLED_STORAGE_KEY || !event.key) {
        handleQuotesUpdate();
      }
    };

    window.addEventListener('welcome_popup_quotes_updated', handleQuotesUpdate);
    window.addEventListener('welcome_popup_enabled_updated', handleEnabledUpdate);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('welcome_popup_quotes_updated', handleQuotesUpdate);
      window.removeEventListener('welcome_popup_enabled_updated', handleEnabledUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    // Handle ESC key to close popup
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when popup is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const getTimeBasedGreeting = (): string => {
    const hour = new Date().getHours();
    
    if (hour >= 0 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else {
      // Use Good Evening from 5 PM (17:00) until midnight
      return 'Good Evening';
    }
  };

  const getTodayDateString = (): string => {
    const today = new Date();
    // Use UTC date for consistency across timezones
    return `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`;
  };

  const checkAndShowPopup = () => {
    try {
      const enabled = localStorage.getItem(ENABLED_STORAGE_KEY);
      if (enabled === 'false' || hasSeenWelcome()) {
        return;
      }

      setGreeting(getTimeBasedGreeting());

      const savedQuotes = localStorage.getItem(QUOTES_STORAGE_KEY);
      let quotes: Quote[] = DEFAULT_QUOTES;
      if (savedQuotes) {
        const parsed = JSON.parse(savedQuotes);
        if (Array.isArray(parsed)) quotes = parsed;
      }

      if (quotes.length === 0) {
        return;
      }

      const selectedQuote = selectQuoteForToday(quotes);
      if (selectedQuote) {
        setQuote(selectedQuote);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Error checking welcome popup:', error);
    }
  };

  const selectQuoteForToday = (quotes: Quote[]): Quote | null => {
    if (quotes.length === 0) return null;
    
    // Use date as seed for consistent selection
    const dateString = getTodayDateString();
    
    // Create a simple hash from the date string
    let hash = 0;
    for (let i = 0; i < dateString.length; i++) {
      const char = dateString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Use absolute value and modulo to get index
    const index = Math.abs(hash) % quotes.length;
    return quotes[index];
  };

  const handleClose = () => {
    rememberWelcome();
    setIsVisible(false);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible || !quote) {
    return null;
  }

  return createPortal(
    <div 
      className="welcome-popup-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      aria-describedby="welcome-popup-quote"
    >
      <div className="welcome-popup-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="welcome-popup-close"
          onClick={handleClose}
          aria-label="Close welcome popup"
          title="Close (ESC)"
        >
          <span aria-hidden="true">×</span>
        </button>
        
        <div className="welcome-popup-content">
          <img
            className="welcome-popup-mark"
            src={MARK_SRC}
            alt=""
            width={52}
            height={52}
          />
          <span className="welcome-popup-rule" aria-hidden="true" />
          <h2 id="welcome-popup-title" className="welcome-popup-title">
            {greeting}
          </h2>
          <blockquote id="welcome-popup-quote" className="welcome-popup-quote">
            <p>{quote.text}</p>
            {quote.author && (
              <footer className="welcome-popup-author">— {quote.author}</footer>
            )}
          </blockquote>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WelcomePopup;
